import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Reveal from '../components/Reveal';
import SubstrateViz from '../components/SubstrateViz';
import { useLanguage } from '../../../hooks/useLanguage';

const Platform: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? 'Data, a domain expert a few hours a week, and a real problem. That is what a pilot starts with.'
          : 'Data, een domeinexpert een paar uur per week, en een echt probleem. Daarmee start een pilot.'}
      </h2>
      <p className="site-end__sub">
        {en
          ? "We work with a small number of partners and only on real problems. If yours fits, we'd like to hear about it."
          : 'We werken met een klein aantal partners en alleen aan echte problemen. Past het bij jullie, dan horen we het graag.'}
      </p>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact?prefill=pilot">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta} hidePartners>
      <Helmet>
        <title>Platform · Thoughtful Oasis</title>
        <meta
          name="description"
          content="The architecture, API surface, integrations and deployment model behind the Thoughtful Oasis ontology layer."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/platform" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Platform · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="A vector ontology layer between your data and your applications. EU-deployable, on-premise available, API-first."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/platform" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
        <style>{`
          /* Two-column hero: text left, substrate viz right, no overlap. */
          .hero--split {
            position: relative;
            display: block;
            min-height: auto;
          }
          .hero--split .hero__inner {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 48px;
            align-items: center;
            max-width: var(--max-w);
            margin: 0 auto;
            padding: 0 var(--pad-x);
            width: 100%;
            position: relative;
            z-index: 2;
          }
          .hero--split .hero__text { min-width: 0; max-width: 100%; }
          .hero--split .hero__text > .label,
          .hero--split .hero__text > h1,
          .hero--split .hero__text > .hero__sub {
            max-width: 36ch;
          }
          .hero--split .hero__viz {
            position: relative;
            top: auto;
            right: auto;
            transform: none;
            width: 100%;
            max-width: 520px;
            margin: 0 auto;
            aspect-ratio: 1 / 1;
            z-index: 1;
            pointer-events: none;
            justify-self: end;
            --line: rgba(15, 18, 16, 0.10);
            --line-2: rgba(15, 18, 16, 0.18);
            --accent: var(--teal);
            --ink-2: #2F3530;
          }
          .hero--split .hero__viz .axis-label,
          .hero--split .hero__viz .entity-label,
          .hero--split .hero__viz .entity-tag {
            font-family: var(--f-mono);
            fill: var(--ink);
            paint-order: stroke;
            stroke: var(--cream);
            stroke-width: 2.5;
            stroke-linejoin: round;
          }
          .hero--split .hero__viz .axis-label   { font-size: 9px;   font-weight: 500; fill: #2F3530; }
          .hero--split .hero__viz .entity-label { font-size: 8.5px; font-weight: 500; fill: var(--ink); }
          .hero--split .hero__viz .entity-tag   { font-size: 7.5px; fill: var(--ink-soft); }
          @media (max-width: 900px) {
            .hero--split .hero__inner {
              grid-template-columns: 1fr;
              gap: 32px;
            }
            .hero--split .hero__viz {
              max-width: 420px;
              opacity: 0.85;
              justify-self: center;
            }
          }
        `}</style>
      </Helmet>

      <header className="hero hero--sub hero--split">
        <div className="hero__inner">
          <div className="hero__text">
            <span className="label">Platform</span>
            <h1 style={{ marginTop: 20 }}>
              {en ? "How it's built, and where it goes." : 'Hoe het gebouwd is, en waar het naartoe gaat.'}
            </h1>
            <p className="hero__sub">
              {en
                ? 'A vector ontology layer that sits between your data and your applications. Every input is encoded into a space whose dimensions a domain expert recognises; every output is traced back to those dimensions.'
                : 'Een vector-ontologie-laag tussen je data en je applicaties. Elke input wordt gecodeerd in een ruimte waarvan een domeinexpert de assen herkent; elke output is herleidbaar naar die assen.'}
            </p>
            <p className="hero__sub">
              {en
                ? 'EU-deployable. On-premise available for regulated environments. API-first, with no UI to adopt.'
                : 'Deploybaar in de EU. On-premise beschikbaar voor gereguleerde omgevingen. API-first, geen UI om in te voeren.'}
            </p>
          </div>
          <SubstrateViz id="platform-hero-viz" className="hero__viz" />
        </div>
      </header>

      {/* Architecture */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Architecture' : 'Architectuur'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Three components, each replaceable.' : 'Drie componenten, elk vervangbaar.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'The substrate is the vector ontology itself. On either side sit two components: one that fills it with meaning, one that computes on it. Either side can be swapped without touching the substrate.'
                : 'De AI-laag zelf is de vector-ontologie. Aan weerszijden zitten twee componenten: één die de laag met betekenis vult, één die erop rekent. Beide kanten kun je vervangen zonder de AI-laag te raken.'}
            </p>
          </Reveal>

          <div className="arch">
            <Reveal className="arch__step">
              <p className="arch__num">01 · Encoders</p>
              <p className="arch__title">
                {en ? 'Read input into the space.' : 'Lees input in de ruimte.'}
              </p>
              <p className="arch__body">
                {en
                  ? 'Modified small language models act as ontological encoders. Given input data and a defined vector ontology, arbitrary data (text, records, sensor traces) is embedded into an N-dimensional interpretable embedding. Deterministic encoders handle structured tabular data.'
                  : 'Aangepaste kleine taalmodellen werken als ontologische encoders. Met inputdata en een gedefinieerde vector-ontologie wordt willekeurige data (tekst, records, sensorstromen) ingebed in een N-dimensionale interpreteerbare embedding. Deterministische encoders verwerken gestructureerde tabel-data.'}
              </p>
            </Reveal>
            <div className="arch__arrow">→</div>
            <Reveal className="arch__step">
              <p className="arch__num">{en ? '02 · Vector ontology' : '02 · Vector-ontologie'}</p>
              <p className="arch__title">
                {en ? 'The interpretable substrate.' : 'De interpreteerbare AI-laag.'}
              </p>
              <p className="arch__body">
                {en
                  ? 'A learned finite-dimensional space whose basis vectors are named, interpretable domain features. Entities, queries and answers all become points.'
                  : 'Een aangeleerde eindig-dimensionale ruimte waarvan de basisvectoren benoemde, interpreteerbare domeinkenmerken zijn. Entiteiten, queries en antwoorden worden allemaal punten.'}
              </p>
            </Reveal>
            <div className="arch__arrow">→</div>
            <Reveal className="arch__step">
              <p className="arch__num">{en ? '03 · Reasoning engines' : '03 · Redeneerengines'}</p>
              <p className="arch__title">
                {en ? 'Compute on the substrate.' : 'Reken op de AI-laag.'}
              </p>
              <p className="arch__body">
                {en
                  ? 'Geometric algorithms designed by hand. Four operations on the ontology: search, classification, prediction, planning. Each output is fully attributable to the dimensions that produced it.'
                  : 'Geometrische algoritmes, met de hand ontworpen. Vier operaties op de ontologie: zoeken, classificeren, voorspellen, plannen. Elke output is volledig te herleiden naar de assen die hem produceerden.'}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Training */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">Training</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'One click to train on your data.' : 'Eén klik om te trainen op jouw data.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'Point the system at your data — documents, records, sensor traces, whatever you have — and the ontology trains itself. No labelling, no feature engineering, no months of data preparation. The domain expert reviews the resulting dimensions; the system handles everything else.'
                : 'Wijs het systeem naar je data — documenten, records, sensorstromen, wat je ook hebt — en de ontologie traint zichzelf. Geen labelling, geen feature engineering, geen maanden datavoorbereiding. De domeinexpert beoordeelt de resulterende dimensies; het systeem regelt de rest.'}
            </p>
          </Reveal>

          <div className="deploy-grid">
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'Arbitrary input' : 'Willekeurige input'}</span>
              <p>
                {en
                  ? 'Text, structured records, PDFs, sensor data, images with metadata — the encoder accepts whatever your domain produces. No upstream schema rewrite required.'
                  : "Tekst, gestructureerde records, PDF's, sensordata, afbeeldingen met metadata — de encoder accepteert wat je domein produceert. Geen upstream schema-herschrijving nodig."}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">
                {en ? 'Automatic dimensions' : 'Automatische dimensies'}
              </span>
              <p>
                {en
                  ? "The system proposes the named axes from your data. A domain expert verifies and adjusts — they don't need to invent them from scratch. The ontology reflects the structure already present in your domain."
                  : 'Het systeem stelt de benoemde assen voor op basis van je data. Een domeinexpert verifieert en past aan — die hoeft ze niet vanaf nul te bedenken. De ontologie weerspiegelt de structuur die al in je domein aanwezig is.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'Fast iteration' : 'Snelle iteratie'}</span>
              <p>
                {en
                  ? 'Retrain as your data changes or your understanding deepens. Each training run produces a versioned ontology. Compare versions, roll back, or branch — the same workflow you know from code.'
                  : 'Hertrain wanneer je data verandert of je inzicht verdiept. Elke trainingsrun levert een geversioneerde ontologie op. Vergelijk versies, draai terug of branch — dezelfde workflow die je kent van code.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'No black box' : 'Geen black box'}</span>
              <p>
                {en
                  ? 'After training, every dimension is readable and auditable. You see exactly what the system learned and can challenge any axis before it goes into production.'
                  : 'Na training is elke dimensie leesbaar en auditeerbaar. Je ziet precies wat het systeem geleerd heeft en kunt elke as ter discussie stellen voordat het in productie gaat.'}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Deployment */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Deployment' : 'Implementatie'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Audit-shaped by default.' : 'Auditeerbaar van opzet.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'Versioned ontologies, role-scoped access, and an immutable audit trace from query to result. Built so a regulator can reconstruct any decision the system made.'
                : 'Geversioneerde ontologieën, rol-gebaseerde toegang, en een onveranderbaar auditspoor van vraag tot antwoord. Gebouwd zodat een toezichthouder elke beslissing van het systeem kan reconstrueren.'}
            </p>
          </Reveal>

          <div className="deploy-grid">
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'Versioning' : 'Versionering'}</span>
              <p>
                {en
                  ? 'Ontologies are versioned objects. A change to a dimension is a versioned change; classifications and retrievals reference the ontology version that produced them. Audits replay against the version of the ontology that was live at the time of the decision.'
                  : 'Ontologieën zijn geversioneerde objecten. Een wijziging aan een as is een geversioneerde wijziging; classificaties en retrievals verwijzen naar de ontologieversie waaruit ze voortkwamen. Audits draaien terug tegen de versie van de ontologie die op dat moment live stond.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">{en ? 'Access' : 'Toegang'}</span>
              <p>
                {en
                  ? 'Role-scoped reads. Domain experts edit dimensions through an admin surface; engineers integrate via the API; auditors see the immutable trace of every classification, retrieval or recommendation.'
                  : 'Rol-gebaseerd lezen. Domeinexperts bewerken assen via een admin-interface; engineers integreren via de API; auditors zien het onveranderbare spoor van elke classificatie, retrieval of aanbeveling.'}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">Hosting</span>
              <p>
                {en
                  ? 'EU-deployable SaaS for default operation. On-premise deployment available for environments where data cannot leave the customer perimeter: security authorities, ministries and clinical institutions.'
                  : "EU-deploybare SaaS voor standaardgebruik. On-premise deployment beschikbaar voor omgevingen waar data het klantperimeter niet mag verlaten: veiligheidsregio's, ministeries en zorginstellingen."}
              </p>
            </Reveal>
            <Reveal className="deploy-grid__cell">
              <span className="label">Observability</span>
              <p>
                {en
                  ? 'Standard logs, metrics and tracing exposed in OpenTelemetry format. Every encoding, retrieval and classification call carries the ontology version, the requesting role, and the named dimensions that drove the result.'
                  : 'Standaard logs, metrics en tracing in OpenTelemetry-formaat. Elke encoding-, retrieval- en classificatie-aanroep draagt de ontologieversie, de aanvragende rol, en de benoemde assen die het resultaat bepaalden.'}
              </p>
            </Reveal>
          </div>

          <p
            className="body-text"
            style={{
              margin: '56px auto 0',
              maxWidth: 'var(--max-w-prose)',
              textAlign: 'center',
              color: 'var(--ink-soft)',
              padding: '0 var(--pad-x)',
            }}
          >
            {en
              ? 'Sub-processors, certifications, incident response, and the full data-handling model live in one place: '
              : 'Sub-verwerkers, certificeringen, incident response en het volledige dataverwerkingsmodel staan op één plek: '}
            <Link className="link-inline" to="/trust">
              {en ? 'Trust & compliance' : 'Vertrouwen & compliance'}
            </Link>
            .
          </p>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default Platform;
